import { useState } from 'react';
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiX } from 'react-icons/fi';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { formatAudioTime } from './audioPlayerUtils';
import MarqueeText from '../common/MarqueeText';

export default function PodcastPlayerBar() {
  const {
    currentEpisode,
    isPlaying,
    progress,
    duration,
    volume,
    isMuted,
    playbackRate,
    isLoading,
    togglePlayPause,
    seek,
    skipTime,
    restartMedia,
    cyclePlaybackRate,
    setVolumeLevel,
    toggleMute,
    closePlayer,
  } = useAudioPlayer();

  const [isSeeking, setIsSeeking] = useState(false);
  const [seekValue, setSeekValue] = useState(0);

  if (!currentEpisode) return null;

  const displayProgress = isSeeking ? seekValue : progress;
  const progressPercent = duration > 0 ? (displayProgress / duration) * 100 : 0;

  const handleSeekStart = (e) => {
    setIsSeeking(true);
    setSeekValue(parseFloat(e.target.value));
  };

  const handleSeekChange = (e) => {
    setSeekValue(parseFloat(e.target.value));
  };

  const handleSeekEnd = (e) => {
    seek(parseFloat(e.target.value));
    setIsSeeking(false);
  };

  return (
    <div className="container mx-auto px-4 py-2.5">
      {/* Desktop / Tablet Layout: 3 Columns (Equal 1/3 columns, perfectly centering controls) */}
      <div className="hidden md:grid md:grid-cols-3 md:items-center md:gap-4">
        {/* Left Column: Title only, left-aligned, bold, no artwork thumbnail, no speaker name */}
        <div className="min-w-0 pr-2">
          <MarqueeText
            text={currentEpisode.title}
            className="font-bold text-gray-900 text-sm"
          />
        </div>

        {/* Center Column: Controls + Centered Scrubber directly below */}
        <div className="flex flex-col items-center gap-1 w-full">
          {/* Top Row: Playback Controls */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Start from beginning (|◀) */}
            <button
              onClick={restartMedia}
              className="p-2 text-gray-600 hover:text-primary transition-colors rounded-full hover:bg-gray-100"
              title="Start from beginning"
              aria-label="Start from beginning"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="19 20 9 12 19 4 19 20" />
                <line x1="5" y1="19" x2="5" y2="5" />
              </svg>
            </button>

            {/* Skip Back 15s */}
            <button
              onClick={() => skipTime(-15)}
              className="p-2 text-gray-600 hover:text-primary transition-colors rounded-full hover:bg-gray-100 flex items-center justify-center relative"
              title="Rewind 15 seconds"
              aria-label="Rewind 15 seconds"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <text
                  x="12"
                  y="15"
                  fill="currentColor"
                  stroke="none"
                  fontSize="7.5"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  15
                </text>
              </svg>
            </button>

            {/* Play / Pause Button */}
            <button
              onClick={togglePlayPause}
              disabled={isLoading}
              className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-transform disabled:opacity-50 shadow-sm mx-1"
              title={isPlaying ? 'Pause' : 'Play'}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <FiPause className="w-5 h-5" />
              ) : (
                <FiPlay className="w-5 h-5 ml-0.5" />
              )}
            </button>

            {/* Skip Forward 30s */}
            <button
              onClick={() => skipTime(30)}
              className="p-2 text-gray-600 hover:text-primary transition-colors rounded-full hover:bg-gray-100 flex items-center justify-center relative"
              title="Skip 30 seconds"
              aria-label="Skip 30 seconds"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.85.99 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <text
                  x="12"
                  y="15"
                  fill="currentColor"
                  stroke="none"
                  fontSize="7.5"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  30
                </text>
              </svg>
            </button>

            {/* Speed Switcher Pill */}
            <button
              onClick={cyclePlaybackRate}
              className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors tabular-nums border border-gray-200/60"
              title="Playback speed"
              aria-label={`Playback speed ${playbackRate}x`}
            >
              {playbackRate}x
            </button>
          </div>

          {/* Bottom Row: Centered Time Scrubber */}
          <div className="flex items-center gap-2.5 w-full">
            <span className="text-xs text-gray-500 tabular-nums w-10 text-right flex-shrink-0">
              {formatAudioTime(displayProgress)}
            </span>
            <div className="relative flex-1 flex items-center group">
              <input
                type="range"
                min="0"
                max={duration || 0}
                step="0.5"
                value={displayProgress}
                onMouseDown={handleSeekStart}
                onChange={handleSeekChange}
                onMouseUp={handleSeekEnd}
                onTouchStart={handleSeekStart}
                onTouchEnd={handleSeekEnd}
                className="w-full h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary
                           [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                           [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary
                           [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:opacity-0
                           group-hover:[&::-webkit-slider-thumb]:opacity-100
                           [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
                           [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary
                           [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:opacity-0
                           group-hover:[&::-moz-range-thumb]:opacity-100"
                style={{
                  background: `linear-gradient(to right, #072441 ${progressPercent}%, #e5e7eb ${progressPercent}%)`,
                }}
              />
            </div>
            <span className="text-xs text-gray-500 tabular-nums w-10 flex-shrink-0">
              {formatAudioTime(duration)}
            </span>
          </div>
        </div>

        {/* Right Column: Volume + Close */}
        <div className="flex items-center gap-2 justify-end">
          <div className="flex items-center gap-1.5">
            <button
              onClick={toggleMute}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
              title={isMuted ? 'Unmute' : 'Mute'}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? (
                <FiVolumeX className="w-4 h-4" />
              ) : (
                <FiVolume2 className="w-4 h-4" />
              )}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume * 100}
              onChange={(e) => setVolumeLevel(parseInt(e.target.value, 10) / 100)}
              className="w-20 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              aria-label="Volume"
            />
          </div>

          <button
            onClick={closePlayer}
            className="p-1.5 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors ml-1"
            title="Close player"
            aria-label="Close player"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="flex md:hidden flex-col gap-2">
        {/* Top: Title + Close */}
        <div className="flex items-center justify-between gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <MarqueeText
              text={currentEpisode.title}
              className="font-bold text-gray-900 text-sm"
            />
          </div>
          <button
            onClick={closePlayer}
            className="p-1 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
            title="Close player"
            aria-label="Close player"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* Middle: Controls */}
        <div className="flex items-center justify-center gap-4 py-0.5">
          <button
            onClick={restartMedia}
            className="p-1.5 text-gray-600 hover:text-primary transition-colors"
            title="Start from beginning"
            aria-label="Start from beginning"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="19 20 9 12 19 4 19 20" />
              <line x1="5" y1="19" x2="5" y2="5" />
            </svg>
          </button>

          <button
            onClick={() => skipTime(-15)}
            className="p-1.5 text-gray-600 hover:text-primary transition-colors flex items-center justify-center relative"
            title="Rewind 15 seconds"
            aria-label="Rewind 15 seconds"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
              <text
                x="12"
                y="15"
                fill="currentColor"
                stroke="none"
                fontSize="7.5"
                fontWeight="bold"
                textAnchor="middle"
              >
                15
              </text>
            </svg>
          </button>

          <button
            onClick={togglePlayPause}
            disabled={isLoading}
            className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-transform disabled:opacity-50 shadow-sm mx-1"
            title={isPlaying ? 'Pause' : 'Play'}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <FiPause className="w-5 h-5" />
            ) : (
              <FiPlay className="w-5 h-5 ml-0.5" />
            )}
          </button>

          <button
            onClick={() => skipTime(30)}
            className="p-1.5 text-gray-600 hover:text-primary transition-colors flex items-center justify-center relative"
            title="Skip 30 seconds"
            aria-label="Skip 30 seconds"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.85.99 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <text
                x="12"
                y="15"
                fill="currentColor"
                stroke="none"
                fontSize="7.5"
                fontWeight="bold"
                textAnchor="middle"
              >
                30
              </text>
            </svg>
          </button>

          <button
            onClick={cyclePlaybackRate}
            className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors tabular-nums border border-gray-200/60"
            title="Playback speed"
            aria-label={`Playback speed ${playbackRate}x`}
          >
            {playbackRate}x
          </button>
        </div>

        {/* Bottom: Scrubber */}
        <div className="flex items-center gap-2 w-full">
          <span className="text-[11px] text-gray-500 tabular-nums w-8 text-right flex-shrink-0">
            {formatAudioTime(displayProgress)}
          </span>
          <div className="relative flex-1 flex items-center group">
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.5"
              value={displayProgress}
              onMouseDown={handleSeekStart}
              onChange={handleSeekChange}
              onMouseUp={handleSeekEnd}
              onTouchStart={handleSeekStart}
              onTouchEnd={handleSeekEnd}
              className="w-full h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary
                         [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                         [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary
                         [&::-webkit-slider-thumb]:appearance-none
                         [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
                         [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary
                         [&::-moz-range-thumb]:border-0"
              style={{
                background: `linear-gradient(to right, #072441 ${progressPercent}%, #e5e7eb ${progressPercent}%)`,
              }}
            />
          </div>
          <span className="text-[11px] text-gray-500 tabular-nums w-8 flex-shrink-0">
            {formatAudioTime(duration)}
          </span>
        </div>
      </div>
    </div>
  );
}
